package com.narbase.letsgo.web.views.linkshare

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.weightOf
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.percent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.drawable.Color
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.passengers.ExtendedPassengerDto
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.datetime.toDate
import com.narbase.letsgo.web.utils.formatDateAndTime
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.views.*
import com.narbase.letsgo.web.views.callCenter.PassengerSelectorViewModel
import com.narbase.letsgo.web.views.user.profile.EditablePhoneNumberView


class PassengerFilterDialog(
    private val onPassengerSelected: (ExtendedPassengerDto) -> Unit
) {
    private var searchByPhoneLayout: LinearLayout? = null
    private var searchByExtensionLayout: LinearLayout? = null
    private val popup by lazy { popUpDialog() }
    private val viewModel = PassengerSelectorViewModel()
    private val phoneEditableView = EditablePhoneNumberView()

    private var extensionSearchInput: TextInput? = null
    private var isSearchByExtension = false

    fun show() {
        theme.showDialog(popup) {
            it.bottomBar = {
                horizontalLayout {
                    matchParentWidth()
                    horizontalFiller()
                    theme.unimportantButton(this) {
                        text = "Dismiss".localized()
                        onClick = { popup.dismissDialog() }
                    }
                }
            }

            theme.bigTextView(this) {
                text = "Find a passenger".localized()
            }
            verticalFiller(4)
            theme.text(this) {
                text = "Search passengers using phone number".localized()
            }
            verticalFiller(16)
            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    backgroundColor = Color.white
                    alignItems = Alignment.Center
                }
                searchByPhoneLayout = verticalLayout {
                    style {
                        minWidth = 65.percent
                    }
                    theme.label(this, "Phone number".localized(), true)
                    form {
                        matchParentWidth()
                        element.onsubmit = {
                            it.preventDefault()
                        }
                        horizontalLayout {
                            style {
                                width = matchParent
                            }

                            mount(phoneEditableView)
                            phoneEditableView.enableEdit()

                            horizontalFiller()

                            theme.menuButton(this) {
                                element.type = "submit"
                                horizontalLayout {
                                    style {
                                        alignSelf = Alignment.Center
                                        padding = 4.px
                                    }
                                    imageView {
                                        style {
                                            alignSelf = Alignment.Center
                                            width = 12.px
                                            height = 12.px
                                        }
                                        element.src = "/public/img/search.png"
                                    }
                                    horizontalFiller(8)
                                    textView {
                                        text = "Search passengers".localized()
                                    }
                                }
                                onClick = {
                                    search()
                                }
                            }
                        }
                    }
                }

                verticalFiller(24)
                searchByExtensionLayout = searchByExtensionLayout()
                verticalFiller(10)

                verticalLayout {
                    matchParentWidth()
                    withLoadingAndError(
                        viewModel.uiState,
                        onRetryClicked = viewModel::getPassenger,
                        onLoaded = { onListLoaded() }
                    )
                }
            }
        }
        onDialogCreated()
    }

    private fun View.searchByExtensionLayout() = verticalLayout {
        matchParentWidth()
        theme.text(this) {
            text = "Search passengers using extensions".localized()
        }
        verticalFiller(16)
        horizontalLayout {
            matchParentWidth()
            style {
                justifyContent = JustifyContent.Center
            }
            verticalLayout {
                style {
                    minWidth = 65.percent
                }
                theme.label(this, "Sip Extension".localized(), true)
                form {
                    matchParentWidth()
                    element.onsubmit = {
                        it.preventDefault()
                    }
                    horizontalLayout {
                        matchParentWidth()
                        extensionSearchInput = theme.textInput(this) {
                            style {
                                flexGrow = "1"
                            }
                        }

                        horizontalFiller(10)

                        theme.menuButton(this) {
                            element.type = "submit"
                            horizontalLayout {
                                style {
                                    alignSelf = Alignment.Center
                                    padding = 4.px
                                }
                                imageView {
                                    style {
                                        alignSelf = Alignment.Center
                                        width = 12.px
                                        height = 12.px
                                    }
                                    element.src = "/public/img/search.png"
                                }
                                horizontalFiller(8)
                                textView {
                                    text = "Search passengers".localized()
                                }
                            }
                            onClick = {
                                searchExtension()
                            }
                        }
                    }
                }
            }
        }
    }

    private fun search() {
        isSearchByExtension = false
        if (phoneEditableView.phoneNumber.trim().isBlank() || phoneEditableView.phoneNumber.trim().length < 5) {
            SnackBar.showText("Phone number is invalid".localized())
            return
        }
        if (phoneEditableView.code.trim().isBlank()) {
            SnackBar.showText("Calling code is invalid".localized())
            return
        }
        viewModel.searchFor(phoneEditableView.code.removePrefix("+"), phoneEditableView.phoneNumber)
    }

    private fun searchExtension() {
        isSearchByExtension = true
        val extension = extensionSearchInput?.text?.trim()

        if (extension == null || extension.isBlank()) {
            SnackBar.showText("Extension is invalid".localized())
            return
        }
        viewModel.getPassengerWithExtension(extension)
    }

    private fun onDialogCreated() {
        phoneEditableView.focusOnPhoneNumberInput()
    }

    private fun View.onListLoaded() {
        clearAllChildren()
        val foundItem = viewModel.data
        if (foundItem != null) {
            searchByPhoneLayout?.isVisible = true
            searchByExtensionLayout?.isVisible = true
            populateFoundPassengerView(foundItem)
        } else {
            horizontalLayout {
                matchParentWidth()
                style {
                    justifyContent = JustifyContent.Center
                    paddingVertical = 24.px
                }
                theme.text(this) {
                    text = "No passenger found".localized()
                }
            }
        }
    }

    private fun View.populateFoundPassengerView(item: ExtendedPassengerDto) {
        verticalLayout {
            style {
                width = matchParent
                pointerCursor()
                padding = 8.px
                border = "1px solid ${AppColors.borderColor}"
                borderRadius = 4.px
                hover { backgroundColor = AppColors.lightBackground }
            }
            theme.bigTextView(this) {
                style { width = matchParent }
                text = item.passenger.fullName
            }

            horizontalLayout {
                matchParentWidth()
                verticalLayout {
                    style { width = weightOf(1) }
                    theme.label(this, "Email / Phone", false)
                    theme.text(this) { text = item.passenger.email }
                }
                horizontalFiller(8)
                verticalLayout {
                    style { width = weightOf(1) }
                    theme.label(this, "Created", false)
                    theme.text(this) { text = item.passenger.createdOn?.toDate()?.formatDateAndTime() ?: "-" }
                }
            }

            onClick = {
                popup.dismissDialog()
                onPassengerSelected(item)
            }
        }
    }
}
